import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const inp = "mt-1 w-full rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";
const lbl = "text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold";
const today = () => new Date().toISOString().slice(0, 10);

export function IssueToTaskDialog({ issue, onClose, endpoint, invalidateKey = "fo-issues", successMsg }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ task_date: today(), due_date: "", trade: "general", assignee: "", notes: "" });
  const { data: meta } = useQuery({ queryKey: ["mt-meta"], queryFn: async () => (await api.get("/maintenance/meta")).data });
  const save = useMutation({
    mutationFn: async () => (await api.post(endpoint || `/fieldops/issues/${issue.id}/to-task`, f)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [invalidateKey] });
      qc.invalidateQueries({ queryKey: ["mt-tasks"] });
      toast.success(successMsg || "Δημιουργήθηκε εργασία συντήρησης — η βλάβη μπήκε «Σε εξέλιξη»");
      onClose();
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={!!issue} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="issue-to-task-dialog">
        <DialogHeader>
          <DialogTitle>Εργασία συντήρησης</DialogTitle>
          <DialogDescription>{issue?.title} · {issue?.unit_name}. Η περιγραφή και οι φωτογραφίες μεταφέρονται στην εργασία.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={lbl}>Ημερομηνία</span>
            <input data-testid="itt-date" type="date" value={f.task_date} onChange={(e) => set("task_date", e.target.value)} className={inp} />
          </label>
          <label className="block"><span className={lbl}>Προθεσμία</span>
            <input data-testid="itt-due" type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className={inp} />
          </label>
          <label className="block"><span className={lbl}>Ειδικότητα</span>
            <select data-testid="itt-trade" value={f.trade} onChange={(e) => set("trade", e.target.value)} className={inp}>
              {(meta?.trades || [{ id: "general", name: "Γενικά" }]).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label className="block"><span className={lbl}>Τεχνικός</span>
            <input data-testid="itt-assignee" value={f.assignee} onChange={(e) => set("assignee", e.target.value)} placeholder="Όνομα τεχνικού" className={inp} />
          </label>
          <label className="block col-span-2"><span className={lbl}>Σημειώσεις</span>
            <textarea data-testid="itt-notes" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className={inp} />
          </label>
        </div>
        {issue?.photos?.length > 0 && <div className="flex flex-wrap gap-2">{issue.photos.map((id) => <img key={id} src={fileUrl(id)} alt="" className="h-12 w-12 object-cover rounded-lg border border-[#E3DED4]" />)}</div>}
        <div className="flex justify-end gap-2">
          <button data-testid="itt-cancel" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm text-[#6F6A60]">Άκυρο</button>
          <button data-testid="itt-submit" disabled={!f.task_date || save.isPending} onClick={() => save.mutate()}
            className="rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50">Δημιουργία εργασίας</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
